/**
 * LanguageSwitcher — 语言切换（全局唯一切换入口）
 * - 写 localStorage allinks_chd_lang
 * - i18n.changeLanguage
 * - 同步替换 URL 前缀 /zh|en/...
 * - N5 方案 A + N4：已登录会话切语言 → 防抖 300ms 后即时 PUT 同步服务端 locale
 *   （防抖期间连续切换只发最后一次；未登录不 PUT，纯本地）
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n, { CHD_LANG_STORAGE_KEY, resolveChdLang, type ChdLang } from '@/i18n';
import { replaceLangInPath } from '@/i18n/localePath';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/Toast';
import { syncLocalePreference } from '@/api/preferences';

/** 切语言服务端同步防抖时长（合同 §6 N4 冻结值 300ms） */
const LOCALE_SYNC_DEBOUNCE_MS = 300;

interface LanguageSwitcherProps {
  /** 布局变体：'compact'（小按钮，适合顶栏）| 'full'（完整样式，适合公开页） */
  variant?: 'compact' | 'full';
  /** 可选：自定义容器 className */
  className?: string;
}

export function LanguageSwitcher({ variant = 'compact', className = '' }: LanguageSwitcherProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [lang, setLang] = useState<ChdLang>(() =>
    resolveChdLang(localStorage.getItem(CHD_LANG_STORAGE_KEY)),
  );
  const syncTimer = useRef<number | null>(null);

  useEffect(() => {
    const onChange = (lng: string) => {
      setLang(resolveChdLang(lng));
    };
    i18n.on('languageChanged', onChange);
    return () => {
      i18n.off('languageChanged', onChange);
      if (syncTimer.current != null) {
        clearTimeout(syncTimer.current);
      }
    };
  }, []);

  const switchLang = (next: ChdLang) => {
    if (next === lang) {
      return;
    }
    localStorage.setItem(CHD_LANG_STORAGE_KEY, next);
    void i18n.changeLanguage(next);
    // N5 方案 A：任意页面切语言均同步服务端（语言真源=服务端）；
    // 未登录（无会话）由 syncLocalePreference 判定跳过 PUT；同步失败不阻塞本地切换
    if (syncTimer.current != null) {
      clearTimeout(syncTimer.current);
    }
    syncTimer.current = window.setTimeout(() => {
      syncTimer.current = null;
      void syncLocalePreference(next).then((outcome) => {
        if (outcome === 'failed') {
          toast.info(t('preferences.localeSyncFailed'));
        }
      });
    }, LOCALE_SYNC_DEBOUNCE_MS);
    const nextPath = replaceLangInPath(location.pathname, next);
    navigate(`${nextPath}${location.search}${location.hash}`, { replace: true });
  };

  if (variant === 'full') {
    return (
      <div className={`inline-flex items-center rounded-lg border border-white/20 overflow-hidden ${className}`}>
        <button
          type="button"
          onClick={() => switchLang('zh')}
          className={`px-3 py-1.5 text-sm font-medium transition-colors ${
            lang === 'zh'
              ? 'bg-white text-slate-900'
              : 'bg-transparent text-slate-300 hover:text-white'
          }`}
        >
          {t('common.langZh')}
        </button>
        <button
          type="button"
          onClick={() => switchLang('en')}
          className={`px-3 py-1.5 text-sm font-medium transition-colors ${
            lang === 'en'
              ? 'bg-white text-slate-900'
              : 'bg-transparent text-slate-300 hover:text-white'
          }`}
        >
          {t('common.langEn')}
        </button>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <button
        type="button"
        onClick={() => switchLang('zh')}
        className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
          lang === 'zh'
            ? 'bg-brand-600 text-white'
            : 'bg-white text-slate-600 hover:bg-slate-100'
        }`}
      >
        {t('common.langZh')}
      </button>
      <button
        type="button"
        onClick={() => switchLang('en')}
        className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
          lang === 'en'
            ? 'bg-brand-600 text-white'
            : 'bg-white text-slate-600 hover:bg-slate-100'
        }`}
      >
        {t('common.langEn')}
      </button>
    </div>
  );
}
